// The studio's edits as pure functions over a ThemeSpec: each returns a new spec and prunes what
// it leaves empty, so an untouched section writes nothing. Surfaces are per mode; accent and the
// background are shared by light and dark and have their own copy for black (a black theme is
// monochrome and true black until the person says otherwise, lib/themeStudio.ts).
import type { Background, StudioMode, ThemeSpec } from '@/kinds/themeprofile/schema'

type Obj = Record<string, unknown>

export type SurfaceKey = 'background' | 'raised' | 'sunken' | 'hairline' | 'text' | 'muted' | 'faint'
export type AccentKey = 'colour' | 'highlight' | 'ok' | 'warn' | 'danger' | 'info'

const prune = (o: Obj): Obj | undefined => {
  const out: Obj = {}
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined) continue
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const p = prune(v as Obj)
      if (p) out[k] = p
    } else out[k] = v
  }
  return Object.keys(out).length ? out : undefined
}

const clean = (spec: Obj): ThemeSpec => (prune(spec) ?? { v: 1 }) as unknown as ThemeSpec

const setMode = (spec: ThemeSpec, mode: StudioMode, key: string, value: unknown): ThemeSpec => {
  const modes = { ...(spec.modes as Obj | undefined) }
  modes[mode] = { ...(modes[mode] as Obj | undefined), [key]: value }
  return clean({ ...spec, modes })
}

export const setSurface = (spec: ThemeSpec, mode: StudioMode, key: SurfaceKey, value: string | undefined) =>
  setMode(spec, mode, 'surfaces', { ...(spec.modes?.[mode]?.surfaces as Obj | undefined), [key]: value })

/** black has an accent of its own; light and dark share the top-level one */
export const accentOf = (spec: ThemeSpec, mode: StudioMode) =>
  mode === 'black' ? spec.modes?.black?.accent : spec.accent

export const setAccent = (spec: ThemeSpec, mode: StudioMode, key: AccentKey, value: string | undefined) => {
  const next = { ...(accentOf(spec, mode) as Obj | undefined), [key]: value }
  return mode === 'black' ? setMode(spec, mode, 'accent', next) : clean({ ...spec, accent: next })
}

export const backgroundOf = (spec: ThemeSpec, mode: StudioMode): Background | undefined =>
  mode === 'black' ? spec.modes?.black?.background : spec.background

export const setBackground = (spec: ThemeSpec, mode: StudioMode, bg: Background | undefined) =>
  mode === 'black' ? setMode(spec, mode, 'background', bg) : clean({ ...spec, background: bg })

/** `trueBlack: false` lets black be tinted; true (the default) is left unset */
export const setTrueBlack = (spec: ThemeSpec, tinted: boolean) =>
  setMode(spec, 'black', 'trueBlack', tinted ? false : undefined)

export const setSection = <K extends Exclude<keyof ThemeSpec, 'v'>>(
  spec: ThemeSpec,
  key: K,
  value: ThemeSpec[K] | undefined,
): ThemeSpec => clean({ ...spec, [key]: value })

/** Which parts of the spec a "Reset section" button clears, per section. */
export const SECTION_KEYS = {
  colours: 'surfaces',
  accent: 'accent',
  background: 'background',
  glass: 'glass',
  shape: 'shape',
  space: 'space',
  type: 'type',
  motion: 'motion',
  icons: 'icons',
} as const
export type SectionId = keyof typeof SECTION_KEYS

/** Clear one section: for the colour and accent sections, in every mode. */
export const resetSectionOf = (spec: ThemeSpec, id: SectionId): ThemeSpec => {
  const key = SECTION_KEYS[id]
  const out: Obj = { ...spec }
  if (id === 'colours' || id === 'accent' || id === 'background') {
    const modes: Obj = {}
    for (const [m, ms] of Object.entries((spec.modes ?? {}) as Obj)) {
      const { [key]: _gone, ...rest } = ms as Obj
      void _gone
      if (id === 'colours') delete rest.trueBlack
      modes[m] = rest
    }
    out.modes = modes
  }
  if (id !== 'colours') delete out[key]
  return clean(out)
}
